import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api, setToken, removeToken, getToken } from '../lib/api';
import { useWishlistStore } from './useWishlistStore';
import { useCartStore } from './useCartStore';
import { getPendingReferral, clearPendingReferral } from '../utils/bookReferral';
import { getPendingAffiliateClick, clearPendingAffiliateClick } from '../utils/affiliateReferral';
import { showRewardToast } from '../utils/rewardToast';

// Fire-and-forget: called once per successful login/session-restore. The
// endpoint itself is idempotent (once per calendar day), so calling it every
// time the app loads is safe — it just quietly no-ops after the first call
// of the day.
function claimDailyLoginReward() {
  api.post('/rewards/daily-login')
    .then((reward) => {
      if (!reward?.awarded) return;
      showRewardToast(reward);
      const { user } = useAuthStore.getState();
      if (user) {
        useAuthStore.setState({ user: { ...user, points: (user.points || 0) + reward.points } });
      }
    })
    .catch(() => { /* non-critical — skip silently */ });
}

// A visitor may click a book referral link while logged out — the code was
// stashed in localStorage (see utils/bookReferral.js and BookDetails.jsx).
// Once they authenticate, re-submit it now that a req.user exists so the
// server can attribute the click to their account.
function flushPendingBookReferral() {
  const pending = getPendingReferral();
  if (!pending?.code) return;
  api.get(`/referrals/track/${pending.code}?bookId=${pending.bookId}`)
    .catch(() => { /* invalid/expired/self-referral — silently drop it */ })
    .finally(() => clearPendingReferral());
}

// Mirrors flushPendingBookReferral above, for an affiliate link (?aff=CODE)
// clicked while logged out (see utils/affiliateReferral.js and
// pages/BookDetails.jsx).
function flushPendingAffiliateClick() {
  const pending = getPendingAffiliateClick();
  if (!pending?.code) return;
  api.get(`/affiliates/track/${pending.code}?bookId=${pending.bookId}`)
    .catch(() => { /* invalid/expired/self-referral — silently drop it */ })
    .finally(() => clearPendingAffiliateClick());
}

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: getToken(),
      isInitialized: false,

      get isAuthenticated() {
        return !!get().user;
      },

      login: async (email, password) => {
        const data = await api.post('/auth/login', { email, password });
        setToken(data.accessToken);
        set({ user: data.user, token: data.accessToken });
        flushPendingBookReferral();
        flushPendingAffiliateClick();
        claimDailyLoginReward();

        // Cart is stored client-side only, per account, so this restores
        // the logged-in user's own saved cart without leaking anyone else's.
        useCartStore.getState().setOwner(data.user.id);

        // Initialize wishlist from server
        try {
          const wishlistData = await api.get('/wishlist');
          const bookIds = (wishlistData.wishlist || []).map(book => book._id || book.id);
          useWishlistStore.getState().initFromServer(bookIds);
        } catch (err) {
          console.error('Failed to initialize wishlist:', err);
        }

        return data;
      },

      register: async (name, email, password, referralCode) => {
        // Validation lives in Signup.jsx (UX) and the server's zod schema
        // (source of truth) — duplicating stricter rules here caused
        // legitimate signups (non-Gmail addresses, valid-but-lowercase
        // passwords) to be rejected client-side before any request was sent.
        // Returns { requiresVerification, email } — no auto-login until OTP verified.
        return api.post('/auth/register', {
          name: name.trim(),
          email: email.trim().toLowerCase(),
          password,
          ...(referralCode?.trim() ? { referralCode: referralCode.trim() } : {}),
        });
      },

      verifyOtp: async (email, otp) => {
        const data = await api.post('/auth/verify-otp', { email, otp });
        setToken(data.accessToken);
        set({ user: data.user, token: data.accessToken });
        flushPendingBookReferral();
        flushPendingAffiliateClick();
        claimDailyLoginReward();
        useCartStore.getState().setOwner(data.user.id);
        useWishlistStore.getState().initFromServer([]);
        return data;
      },

      resendOtp: async (email) => {
        return api.post('/auth/resend-otp', { email });
      },

      forgotPassword: async (email) => {
        return api.post('/auth/forgot-password', { email });
      },

      resetPassword: async (email, otp, password) => {
        return api.post('/auth/reset-password', { email, otp, password });
      },

      logout: async () => {
        try {
          await api.post('/auth/logout');
        } catch {
          // Continue with local logout even if server call fails
        }
        removeToken();
        set({ user: null, token: null });

        // Switch to the guest cart (saving this user's cart for next time)
        // and clear the wishlist store so the next user on this browser
        // doesn't see the previous account's items
        useCartStore.getState().setOwner();
        useWishlistStore.getState().initFromServer([]);
      },

      fetchUser: async () => {
        // Always attempt this, even with no local access token — the
        // request 401s, which api.js's interceptor treats as a cue to try
        // the httpOnly refresh-token cookie. Short-circuiting here on a
        // missing access token skipped that recovery path entirely, so a
        // session with a perfectly valid refresh cookie (up to 24h) but a
        // cleared/missing access token looked permanently signed out.
        try {
          const data = await api.get('/auth/me');
          // Re-read rather than reuse a token captured before this request —
          // /auth/me may have silently refreshed it via the 401 interceptor.
          set({ user: data.user, token: getToken(), isInitialized: true });
          useCartStore.getState().setOwner(data.user.id);
          claimDailyLoginReward();

          // Initialize wishlist from server
          try {
            const wishlistData = await api.get('/wishlist');
            const bookIds = (wishlistData.wishlist || []).map(book => book._id || book.id);
            useWishlistStore.getState().initFromServer(bookIds);
          } catch (err) {
            console.error('Failed to initialize wishlist:', err);
          }
        } catch {
          removeToken();
          set({ user: null, token: null, isInitialized: true });
          useCartStore.getState().setOwner();
        }
      },

      setUser: (user) => set({ user }),
      setToken: (token) => {
        setToken(token);
        set({ token });
      },
    }),
    {
      name: 'geniusmind-auth',
      // Persisting `user` (not just `token`) lets pages that gate on
      // purchase/membership status (e.g. BookDetails' Buy Now/Read Now)
      // render correctly from cache immediately on load, instead of
      // waiting out fetchUser()'s /auth/me round-trip — which can take up
      // to 60s on a cold Render free-tier instance. fetchUser() still runs
      // in the background and reconciles/clears this if it's stale.
      partialize: (state) => ({ token: state.token, user: state.user }),
    }
  )
);

// Initialize on store creation
const { fetchUser, isInitialized } = useAuthStore.getState();
if (!isInitialized) {
  fetchUser();
}
